/*
 * What a template form knows about a gateway type's configuration schema:
 * loading it, grouping it, turning typed values into a json_config body
 * and back, and checking the format of what was typed. Shared by the
 * create and the detail dialogs so both serialise the same way.
 */
import { configFieldsResponse } from '../../config/configSchema';
import { typesService, HardwareFieldDefinition } from '../../services/typesService';
import { JsonConfig } from '../../types/configTemplate';

export const DEFAULT_GROUPING_NAME = 'General';
/** The device password is edited by its own row (random per device, or a value). */
export const PASSWORD_FIELD = 'password';
/** Keys never prefilled into an edit form: the user must retype them. */
const SECRET_FIELDS = new Set([PASSWORD_FIELD, 'mqtt_pass']);

export type FieldValues = Record<string, string>;
export type FieldErrors = Record<string, string | undefined>;

export interface SchemaGroup {
  name: string;
  fields: HardwareFieldDefinition[];
}

export interface LoadedSchema {
  fields: HardwareFieldDefinition[];
  /** The product's base configuration (schema v3.1), the first layer of every device. */
  productConfig: Record<string, unknown>;
}

/** The schema of a gateway type, sorted by group then display order; empty when the type is unknown. */
export const loadTemplateSchema = async (deviceType: string): Promise<LoadedSchema> => {
  const types = await typesService.getHardwareTypes(true);
  const hw = types.find((hwType) => hwType.code === deviceType);
  if (!hw) return { fields: [], productConfig: {} };
  const response = await configFieldsResponse(hw.id, hw.code);
  return { fields: sortSchema(response.items), productConfig: hw.config || {} };
};

export const sortSchema = (items: HardwareFieldDefinition[]): HardwareFieldDefinition[] =>
  [...items].sort((a, b) => {
    const groupCmp = (a.grouping_name || DEFAULT_GROUPING_NAME).localeCompare(b.grouping_name || DEFAULT_GROUPING_NAME);
    if (groupCmp !== 0) return groupCmp;
    return (a.display_order || 0) - (b.display_order || 0);
  });

/** The fields by group, the password left out (it has its own row). */
export const groupSchema = (schema: HardwareFieldDefinition[] | null): SchemaGroup[] => {
  if (!schema) return [];
  const byGroup = new Map<string, HardwareFieldDefinition[]>();
  for (const def of schema) {
    if (def.field_name === PASSWORD_FIELD) continue;
    const name = def.grouping_name || DEFAULT_GROUPING_NAME;
    if (!byGroup.has(name)) byGroup.set(name, []);
    byGroup.get(name)!.push(def);
  }
  return Array.from(byGroup.entries()).map(([name, fields]) => ({ name, fields })).filter((g) => g.fields.length > 0);
};

export const hasPasswordField = (schema: HardwareFieldDefinition[] | null): boolean =>
  Boolean(schema?.some((def) => def.field_name === PASSWORD_FIELD));

/** A dropdown's options, stored either as a JSON array or already parsed. */
export const parseOptions = (opt: unknown): string[] => {
  if (!opt) return [];
  if (Array.isArray(opt)) return opt.map(String);
  if (typeof opt === 'string') {
    try {
      const parsed = JSON.parse(opt);
      if (Array.isArray(parsed)) return parsed.map(String);
    } catch {
      /* not JSON: no options */
    }
  }
  return [];
};

/** The schema defaults as form values, for a new template. */
export const schemaDefaults = (schema: HardwareFieldDefinition[]): FieldValues => {
  const values: FieldValues = {};
  for (const def of schema) {
    if (def.default_value !== undefined && def.default_value !== null && def.default_value !== '') {
      values[def.field_name] = String(def.default_value);
    }
  }
  return values;
};

/** A stored json_config as form values; secrets and custom fields stay out. */
export const configToFieldValues = (config: JsonConfig | null | undefined): FieldValues => {
  const values: FieldValues = {};
  for (const [key, val] of Object.entries(config || {})) {
    if (key === 'custom_fields' || SECRET_FIELDS.has(key)) continue;
    if (val === null || val === undefined) continue;
    values[key] = typeof val === 'object' ? JSON.stringify(val) : String(val);
  }
  return values;
};

/**
 * Form values into a json_config body, on top of `base`. An empty value
 * means "not set by this template": the key is left out (or removed from
 * the base). Numbers and booleans are typed, the rest stays text.
 */
export const fieldValuesToJsonConfig = (schema: HardwareFieldDefinition[] | null, values: FieldValues, base: JsonConfig = {}): JsonConfig => {
  const config = { ...base } as Record<string, unknown>;
  for (const def of schema || []) {
    if (def.field_name === PASSWORD_FIELD) continue;
    const raw = values[def.field_name];
    if (raw === undefined || raw === null || raw === '') {
      delete config[def.field_name];
      continue;
    }
    if (def.value_type === 'port' || def.value_type === 'number') {
      const n = Number(raw);
      config[def.field_name] = Number.isNaN(n) ? raw : n;
    } else if (def.value_type === 'boolean') {
      config[def.field_name] = raw === 'true';
    } else {
      config[def.field_name] = raw;
    }
  }
  return config as JsonConfig;
};

type Translate = (key: any, params?: Record<string, string | number>) => string;

/**
 * Only the format of what was typed is checked: a template is partial, so
 * an empty field is never an error.
 */
export const validateFieldFormats = (schema: HardwareFieldDefinition[] | null, values: FieldValues, t: Translate): FieldErrors => {
  const errors: FieldErrors = {};
  for (const def of schema || []) {
    const raw = (values[def.field_name] ?? '').toString();
    if (!raw.trim()) continue;
    const field = def.display_name || def.field_name;
    if (def.value_type === 'port' || def.field_name.endsWith('_port')) {
      const n = Number(raw);
      if (Number.isNaN(n) || n < 1 || n > 65535) errors[def.field_name] = t('configTemplates.validation.portRange', { field });
    }
    if (def.value_type === 'number') {
      const n = Number(raw);
      if (Number.isNaN(n)) {
        errors[def.field_name] = t('configTemplates.validation.number', { field });
      } else {
        if (def.min_value != null && n < def.min_value) errors[def.field_name] = t('configTemplates.validation.min', { field, value: def.min_value });
        if (def.max_value != null && n > def.max_value) errors[def.field_name] = t('configTemplates.validation.max', { field, value: def.max_value });
      }
    }
  }
  return errors;
};

/** The template name rules shared by both dialogs. */
export const validateTemplateName = (name: string, t: Translate, max = 100): string | undefined => {
  const trimmed = name.trim();
  if (!trimmed) return t('configTemplates.validation.nameRequired');
  if (trimmed.length < 2) return t('configTemplates.validation.nameMin');
  if (trimmed.length > max) return t('configTemplates.validation.nameMax');
  return undefined;
};
